import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, Box, MenuItem, TextField, Chip, Stack, Alert, CircularProgress
} from '@mui/material';
import { AssignmentInd as AssignedIcon } from '@mui/icons-material';

const UnifyAssigneeDialog = ({ open, onClose, customerGroup, salesExecs, onUnify }) => {
  const [selectedUser, setSelectedUser] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (open) {
      // Default to the first assignee if available, or empty
      const firstAssignee = customerGroup?.assignedUsers?.[0]?._id || customerGroup?.assignedUsers?.[0]?.id || '';
      setSelectedUser(firstAssignee ? String(firstAssignee) : '');
      setLoading(false);
    }
  }, [open, customerGroup]);

  if (!customerGroup) return null;

  const handleConfirm = async () => {
    if (!selectedUser) return;
    setLoading(true);
    try {
      await onUnify(customerGroup, [selectedUser]);
      onClose();
    } catch (err) {
      console.error('Failed to unify assignee:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <Box sx={{
          p: 1, borderRadius: 2, bgcolor: '#e0e7ff', color: '#4338ca',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <AssignedIcon fontSize="small" />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Unify Lead Assignment
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Assign all trips of <strong>{customerGroup.guest_name}</strong> to one agent
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
          Customer <strong>{customerGroup.guest_name}</strong> has{' '}
          <strong>{customerGroup.inquiriesCount} separate inquiries</strong>.
          Assigning them to a single executive prevents customer confusion and duplicate calls.
        </Alert>

        {customerGroup.assignedUsers?.length > 1 && (
          <Box sx={{ mb: 2.5, p: 2, bgcolor: '#fffbeb', borderRadius: 2, border: '1px solid #fef3c7' }}>
            <Typography variant="caption" fontWeight={700} color="#b45309" sx={{ display: 'block', mb: 1, textTransform: 'uppercase' }}>
              Currently Assigned To Multiple Agents:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {customerGroup.assignedUsers.map(u => (
                <Chip
                  key={u._id || u.id}
                  label={u.fullName}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              ))}
            </Stack>
          </Box>
        )}

        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          Select Primary Sales Executive for all {customerGroup.inquiriesCount} inquiries:
        </Typography>

        <TextField
          select
          fullWidth
          size="small"
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
          label="Assign All Inquiries To"
        >
          {salesExecs.map(u => (
            <MenuItem key={u.id || u._id} value={String(u.id || u._id)}>
              {u.fullName} {u.permission ? `(${u.permission})` : ''}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedUser || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
            fontWeight: 600,
            px: 3
          }}
        >
          {loading ? 'Reassigning...' : `Assign All ${customerGroup.inquiriesCount} Trips`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnifyAssigneeDialog;
